import React, { useState } from 'react'
import { Helmet } from 'react-helmet'
import '../styles/global.css'
import { saveSponsorContactToSupabase } from '../services/emailService'

const SponsorsPage = () => {
  const [formData, setFormData] = useState({
    companyName: '',
    contactPerson: '',
    email: '',
    phone: '',
    sector: '',
    budget: '',
    comments: '',
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitSuccess, setSubmitSuccess] = useState(false)
  const [submitError, setSubmitError] = useState(null)

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsSubmitting(true)
    setSubmitError(null)

    try {
      // 1. Send form data to Make.com webhook using URLSearchParams to avoid CORS issues
      const formParams = new URLSearchParams()

      // Add all form fields to the URLSearchParams object
      Object.entries(formData).forEach(([key, value]) => {
        formParams.append(key, value)
      })

      const response = await fetch(
        'https://hook.eu2.make.com/ssdun7d1wuohc7xqtzm3vc45hwziaw84',
        {
          method: 'POST',
          body: formParams,
        }
      )

      if (!response.ok) {
        throw new Error('Error al enviar el formulario a Make.com')
      }

      console.log('Form submitted to Make.com successfully:', formData)

      // 2. Save data to Supabase
      const supabaseResult = await saveSponsorContactToSupabase(formData)

      if (!supabaseResult.success) {
        console.error('Error al guardar en Supabase:', supabaseResult.error)
        // Although Supabase failed, the webhook worked, so we can show success
        // but log the error for debugging
      } else {
        console.log('Form data saved to Supabase successfully')
      }

      // 3. Show success message and reset form
      setSubmitSuccess(true)
      setFormData({
        companyName: '',
        contactPerson: '',
        email: '',
        phone: '',
        sector: '',
        budget: '',
        comments: '',
      })
    } catch (error) {
      console.error('Error submitting form:', error)
      setSubmitError(
        'Hubo un error al enviar el formulario. Por favor, inténtalo de nuevo.'
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className='container mx-auto px-4 py-8 md:py-16'>
      <Helmet>
        <title>
          Patrocina en Conversor IAE CNAE | Alcanza a miles de profesionales
        </title>
        <meta
          name='description'
          content='Promociona tu marca en Conversor IAE CNAE y llega a miles de autónomos, empresas y profesionales que buscan información fiscal y económica cada día.'
        />
      </Helmet>

      <h1 className='text-2xl md:text-4xl font-bold mb-6 text-text-primary text-center'>
        Patrocina Conversor IAE CNAE
      </h1>
      <p className='text-text-secondary text-lg mb-12 max-w-2xl mx-auto text-center'>
        Promociona tu marca y servicios ante una audiencia altamente cualificada
        de autónomos, empresas y profesionales del sector económico-fiscal.
      </p>

      {/* Stats Section */}
      <section className='py-8 md:py-12 bg-surface rounded-lg p-8 mb-12'>
        <h2 className='text-xl md:text-3xl font-bold mb-8 text-text-primary text-center'>
          Estadísticas Impactantes
        </h2>

        <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6'>
          <div className='bg-background p-6 rounded-md text-center'>
            <div className='text-primary text-3xl md:text-4xl font-bold mb-2'>
              3,48k+
            </div>
            <div className='text-text-secondary'>Clics mensuales</div>
          </div>

          <div className='bg-background p-6 rounded-md text-center'>
            <div className='text-primary text-3xl md:text-4xl font-bold mb-2'>
              101k
            </div>
            <div className='text-text-secondary'>Impresiones mensuales</div>
          </div>

          <div className='bg-background p-6 rounded-md text-center'>
            <div className='text-primary text-3xl md:text-4xl font-bold mb-2'>
              4.480+
            </div>
            <div className='text-text-secondary'>Usuarios únicos mensuales</div>
          </div>

          <div className='bg-background p-6 rounded-md text-center'>
            <div className='text-primary text-3xl md:text-4xl font-bold mb-2'>
              3,4%
            </div>
            <div className='text-text-secondary'>CTR promedio</div>
          </div>
        </div>
      </section>

      {/* Audience Section */}
      <section className='py-8 md:py-12 mb-12'>
        <h2 className='text-xl md:text-3xl font-bold mb-8 text-text-primary text-center'>
          Perfil Demográfico de Nuestra Audiencia
        </h2>

        <div className='grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto'>
          <div className='bg-surface p-6 rounded-md'>
            <h3 className='text-lg font-bold mb-4 text-primary'>
              Autónomos y Empresas
            </h3>
            <p className='text-text-secondary'>
              Profesionales y empresas que buscan información fiscal y económica
              actualizada para sus trámites administrativos y obligaciones
              fiscales.
            </p>
          </div>

          <div className='bg-surface p-6 rounded-md'>
            <h3 className='text-lg font-bold mb-4 text-primary'>
              Profesionales en Proceso de Alta
            </h3>
            <p className='text-text-secondary'>
              Emprendedores y profesionales en proceso de alta o modificación de
              actividad económica que necesitan identificar los códigos
              correctos.
            </p>
          </div>

          <div className='bg-surface p-6 rounded-md'>
            <h3 className='text-lg font-bold mb-4 text-primary'>
              Consultores y Gestores
            </h3>
            <p className='text-text-secondary'>
              Asesores fiscales, gestores administrativos y consultores que
              utilizan nuestra herramienta para sus clientes y trámites
              profesionales.
            </p>
          </div>
        </div>
      </section>

      {/* Sponsorship Options */}
      <section className='py-8 md:py-12 bg-surface rounded-lg p-8 mb-12'>
        <h2 className='text-xl md:text-3xl font-bold mb-8 text-text-primary text-center'>
          Opciones de Patrocinio
        </h2>

        <div className='grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto'>
          <div className='bg-background p-6 rounded-md border-t-4 border-primary'>
            <h3 className='text-xl font-bold mb-2 text-primary'>
              Pack Premium
            </h3>
            <p className='text-text-secondary mb-4'>
              Banner principal con máxima visibilidad en todas las páginas del
              sitio.
            </p>
            <ul className='text-text-secondary space-y-2'>
              <li className='flex items-start'>
                <svg
                  className='h-5 w-5 text-primary mr-2 mt-0.5'
                  fill='currentColor'
                  viewBox='0 0 20 20'
                >
                  <path
                    fillRule='evenodd'
                    d='M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z'
                    clipRule='evenodd'
                  />
                </svg>
                Presencia en la página de inicio
              </li>
              <li className='flex items-start'>
                <svg
                  className='h-5 w-5 text-primary mr-2 mt-0.5'
                  fill='currentColor'
                  viewBox='0 0 20 20'
                >
                  <path
                    fillRule='evenodd'
                    d='M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z'
                    clipRule='evenodd'
                  />
                </svg>
                Visibilidad en todas las páginas de resultados
              </li>
              <li className='flex items-start'>
                <svg
                  className='h-5 w-5 text-primary mr-2 mt-0.5'
                  fill='currentColor'
                  viewBox='0 0 20 20'
                >
                  <path
                    fillRule='evenodd'
                    d='M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z'
                    clipRule='evenodd'
                  />
                </svg>
                Informe mensual de rendimiento
              </li>
            </ul>
          </div>

          <div className='bg-background p-6 rounded-md border-t-4 border-secondary'>
            <h3 className='text-xl font-bold mb-2 text-secondary'>
              Pack Estándar
            </h3>
            <p className='text-text-secondary mb-4'>
              Banners laterales en páginas específicas con alta relevancia.
            </p>
            <ul className='text-text-secondary space-y-2'>
              <li className='flex items-start'>
                <svg
                  className='h-5 w-5 text-secondary mr-2 mt-0.5'
                  fill='currentColor'
                  viewBox='0 0 20 20'
                >
                  <path
                    fillRule='evenodd'
                    d='M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z'
                    clipRule='evenodd'
                  />
                </svg>
                Presencia en páginas de directorios
              </li>
              <li className='flex items-start'>
                <svg
                  className='h-5 w-5 text-secondary mr-2 mt-0.5'
                  fill='currentColor'
                  viewBox='0 0 20 20'
                >
                  <path
                    fillRule='evenodd'
                    d='M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z'
                    clipRule='evenodd'
                  />
                </svg>
                Visibilidad en páginas de detalles
              </li>
              <li className='flex items-start'>
                <svg
                  className='h-5 w-5 text-secondary mr-2 mt-0.5'
                  fill='currentColor'
                  viewBox='0 0 20 20'
                >
                  <path
                    fillRule='evenodd'
                    d='M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z'
                    clipRule='evenodd'
                  />
                </svg>
                Informe trimestral de rendimiento
              </li>
            </ul>
          </div>

          <div className='bg-background p-6 rounded-md border-t-4 border-iae'>
            <h3
              className='text-xl font-bold mb-2'
              style={{ color: 'var(--color-iae)' }}
            >
              Pack Sectorial
            </h3>
            <p className='text-text-secondary mb-4'>
              Patrocinio en categorías CNAE/IAE específicas relacionadas con su
              sector.
            </p>
            <ul className='text-text-secondary space-y-2'>
              <li className='flex items-start'>
                <svg
                  className='h-5 w-5 mr-2 mt-0.5'
                  style={{ color: 'var(--color-iae)' }}
                  fill='currentColor'
                  viewBox='0 0 20 20'
                >
                  <path
                    fillRule='evenodd'
                    d='M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z'
                    clipRule='evenodd'
                  />
                </svg>
                Presencia en códigos de su sector
              </li>
              <li className='flex items-start'>
                <svg
                  className='h-5 w-5 mr-2 mt-0.5'
                  style={{ color: 'var(--color-iae)' }}
                  fill='currentColor'
                  viewBox='0 0 20 20'
                >
                  <path
                    fillRule='evenodd'
                    d='M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z'
                    clipRule='evenodd'
                  />
                </svg>
                Audiencia altamente segmentada
              </li>
              <li className='flex items-start'>
                <svg
                  className='h-5 w-5 mr-2 mt-0.5'
                  style={{ color: 'var(--color-iae)' }}
                  fill='currentColor'
                  viewBox='0 0 20 20'
                >
                  <path
                    fillRule='evenodd'
                    d='M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z'
                    clipRule='evenodd'
                  />
                </svg>
                Estadísticas específicas del sector
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className='py-8 md:py-12 max-w-2xl mx-auto'>
        <h2 className='text-xl md:text-3xl font-bold mb-8 text-text-primary text-center'>
          Contacta con Nosotros
        </h2>

        {submitSuccess ? (
          <div className='bg-green-900 bg-opacity-20 border border-primary text-primary p-4 rounded-md mb-6 text-center'>
            <p className='font-medium'>
              ¡Gracias por tu interés! Hemos recibido tu solicitud y nos
              pondremos en contacto contigo en las próximas 24-48 horas.
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className='bg-surface p-6 rounded-lg'>
            {submitError && (
              <div className='bg-red-900 bg-opacity-20 border border-red-500 text-red-500 p-4 rounded-md mb-6'>
                <p>{submitError}</p>
              </div>
            )}

            <div className='mb-4'>
              <label
                htmlFor='companyName'
                className='block text-text-primary mb-2'
              >
                Nombre de empresa *
              </label>
              <input
                type='text'
                id='companyName'
                name='companyName'
                value={formData.companyName}
                onChange={handleChange}
                required
                className='w-full bg-background border border-color-border rounded-md p-3 text-text-primary'
              />
            </div>

            <div className='mb-4'>
              <label
                htmlFor='contactPerson'
                className='block text-text-primary mb-2'
              >
                Persona de contacto *
              </label>
              <input
                type='text'
                id='contactPerson'
                name='contactPerson'
                value={formData.contactPerson}
                onChange={handleChange}
                required
                className='w-full bg-background border border-color-border rounded-md p-3 text-text-primary'
              />
            </div>

            <div className='grid grid-cols-1 md:grid-cols-2 gap-4 mb-4'>
              <div>
                <label htmlFor='email' className='block text-text-primary mb-2'>
                  Email *
                </label>
                <input
                  type='email'
                  id='email'
                  name='email'
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className='w-full bg-background border border-color-border rounded-md p-3 text-text-primary'
                />
              </div>

              <div>
                <label htmlFor='phone' className='block text-text-primary mb-2'>
                  Teléfono
                </label>
                <input
                  type='tel'
                  id='phone'
                  name='phone'
                  value={formData.phone}
                  onChange={handleChange}
                  className='w-full bg-background border border-color-border rounded-md p-3 text-text-primary'
                />
              </div>
            </div>

            <div className='mb-4'>
              <label htmlFor='sector' className='block text-text-primary mb-2'>
                Sector de actividad *
              </label>
              <input
                type='text'
                id='sector'
                name='sector'
                value={formData.sector}
                onChange={handleChange}
                required
                className='w-full bg-background border border-color-border rounded-md p-3 text-text-primary'
              />
            </div>

            <div className='mb-4'>
              <label htmlFor='budget' className='block text-text-primary mb-2'>
                Presupuesto estimado
              </label>
              <select
                id='budget'
                name='budget'
                value={formData.budget}
                onChange={handleChange}
                className='w-full bg-background border border-color-border rounded-md p-3 text-text-primary'
              >
                <option value=''>Selecciona un rango</option>
                <option value='menos-500'>Menos de 500€</option>
                <option value='500-1000'>500€ - 1.000€</option>
                <option value='1000-2000'>1.000€ - 2.000€</option>
                <option value='mas-2000'>Más de 2.000€</option>
              </select>
            </div>

            <div className='mb-6'>
              <label
                htmlFor='comments'
                className='block text-text-primary mb-2'
              >
                Comentarios adicionales
              </label>
              <textarea
                id='comments'
                name='comments'
                value={formData.comments}
                onChange={handleChange}
                rows='4'
                className='w-full bg-background border border-color-border rounded-md p-3 text-text-primary'
              ></textarea>
            </div>

            <div className='text-center'>
              <button
                type='submit'
                disabled={isSubmitting}
                className='bg-primary text-white py-3 px-8 rounded-md font-medium hover:bg-opacity-90 transition-colors duration-300 disabled:opacity-70'
              >
                {isSubmitting ? 'Enviando...' : 'Enviar Solicitud'}
              </button>
            </div>
          </form>
        )}

        <div className='mt-8 text-center text-text-secondary'>
          <p>
            También puedes contactarnos directamente en{' '}
            <a href='mailto:brian@conversoriaecnae.es' className='text-primary'>
              brian@conversoriaecnae.es
            </a>
          </p>
        </div>
      </section>
    </div>
  )
}

export default SponsorsPage
